import { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Input } from './ui/input'
import { AppIcon } from './ui/icon'
import {
  useComplianceRules,
  useComplianceControls,
  SEVERITY_CONFIG,
  KIND_RESOLVED_CONFIG,
  lifecycleGroupOf,
  type RuleKindResolved,
  type RuleLifecycleGroup,
} from '../hooks/useComplianceApi'
import { useAssetTypes } from '../api/queries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useTranslation } from 'react-i18next'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { mdiLoading, mdiPlus, mdiScaleBalance } from '@mdi/js'

export function ComplianceRulesPage() {
  const { org } = useParams({ strict: false })
  const { locale } = useLocale()
  const { t } = useTranslation('compliance')
  const navigate = useNavigate()

  const getLocalizedName = useCallback((name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed'
    return name[locale] || name.en || name.fr || Object.values(name)[0] || 'Unnamed'
  }, [locale])
  const search = useSearch({ strict: false }) as { control_code?: string } | undefined
  const [controlFilter, setControlFilter] = useState<string>('all')
  const [lifecycleFilter, setLifecycleFilter] = useState<RuleLifecycleGroup>('active')
  const [searchQuery, setSearchQuery] = useState('')

  const { data: controls = [] } = useComplianceControls(org || 'demo')
  const { data: assetTypes = [] } = useAssetTypes(org || 'demo')

  // Resolve control_code from URL search param to control uid
  useEffect(() => {
    if (search?.control_code && controls.length > 0) {
      const control = controls.find(c => c.code === search.control_code)
      if (control) {
        setControlFilter(control.uid)
      }
    }
  }, [search?.control_code, controls])

  const controlUidForQuery = controlFilter !== 'all' ? controlFilter : undefined
  const { data: rules = [], isLoading, error } = useComplianceRules(org || 'demo', controlUidForQuery)

  // Resolve the active control for breadcrumb
  const activeControl = useMemo(() => {
    if (controlFilter !== 'all') {
      return controls.find(c => c.uid === controlFilter)
    }
    return undefined
  }, [controlFilter, controls])

  useBreadcrumbs(useMemo(() => {
    const crumbs: { title: string; path?: string }[] = [
      { title: t('breadcrumb.home'), path: `/${org}` },
      { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
      { title: t('breadcrumb.compliance'), path: `/${org}/compliance-controls` },
    ]
    if (activeControl) {
      crumbs.push({ title: getLocalizedName(activeControl.name) })
    } else {
      crumbs.push({ title: t('breadcrumb.all_rules') })
    }
    return crumbs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, activeControl, t]))

  const controlMap = useMemo(() => {
    const m = new Map<string, string>()
    controls.forEach(c => m.set(c.uid, getLocalizedName(c.name)))
    return m
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls])

  const assetTypeMap = useMemo(() => {
    const m = new Map<string, string>()
    assetTypes.forEach(at => {
      const name = typeof at.name === 'string' ? at.name : getLocalizedName(at.name as Record<string, string>)
      m.set(at.uid, name)
    })
    return m
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTypes])

  const filteredRules = useMemo(() => {
    let pool = rules
    // Lifecycle filter — defaults to 'active' so historical + at_install
    // collapse out of the day-to-day list. Spec 04 §3.6.
    if (lifecycleFilter !== 'all') {
      pool = pool.filter(r => lifecycleGroupOf(
        (r as { kind_resolved?: RuleKindResolved }).kind_resolved
      ) === lifecycleFilter)
    }
    if (!searchQuery.trim()) return pool
    const q = searchQuery.toLowerCase()
    return pool.filter(r =>
      (r.code || '').toLowerCase().includes(q) ||
      getLocalizedName(r.label).toLowerCase().includes(q)
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules, searchQuery, lifecycleFilter])

  const handleControlFilterChange = (value: string) => {
    setControlFilter(value)
    // Update URL search params
    if (value === 'all') {
      navigate({
        to: '/$org/compliance-rules',
        params: { org: org || 'demo' },
        search: { control_code: undefined },
      })
    } else {
      const control = controls.find(c => c.uid === value)
      if (control) {
        navigate({
          to: '/$org/compliance-rules',
          params: { org: org || 'demo' },
          search: { control_code: control.code },
        })
      }
    }
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.compliance}
            title={t('rules.title')}
            description={t('rules.subtitle')}
            actions={
              <>
          <Can perm="compliance.write">
            <Link to="/$org/compliance-rules/create" params={{ org: org || 'demo' }}>
              <Button data-testid="create-rule-btn">
                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                {t('rules.create')}
              </Button>
            </Link>
          </Can>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4">
            <Input
              placeholder={t('rules.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="rules-search"
            />
            <Select value={controlFilter} onValueChange={handleControlFilterChange}>
              <SelectTrigger className="w-[250px]" data-testid="control-filter">
                <SelectValue placeholder={t('rules.filter_control')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('rules.all_controls')}</SelectItem>
                {controls.map(c => (
                  <SelectItem key={c.uid} value={c.uid}>{getLocalizedName(c.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={lifecycleFilter}
              onValueChange={v => setLifecycleFilter(v as RuleLifecycleGroup)}
            >
              <SelectTrigger className="w-[200px]" data-testid="lifecycle-filter">
                <SelectValue placeholder={t('rules.filter_lifecycle')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('rules.lifecycle_active')}</SelectItem>
                <SelectItem value="installation">{t('rules.lifecycle_installation')}</SelectItem>
                <SelectItem value="historical">{t('rules.lifecycle_historical')}</SelectItem>
                <SelectItem value="all">{t('rules.lifecycle_all')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('rules.error')}</div>
          ) : filteredRules.length === 0 ? (
            <div className="text-muted-foreground text-center py-8">
              {rules.length === 0 ? (
                <EmptyState
                  variant="inline"
                  tone="solid"
                  icon={mdiScaleBalance}
                  title={t('rules.empty_title')}
                  action={
                    <Can perm="compliance.write">
                      <Link to="/$org/compliance-rules/create" params={{ org: org || 'demo' }}>
                        <Button data-testid="create-first-rule-btn">
                          <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                          {t('rules.create')}
                        </Button>
                      </Link>
                    </Can>
                  }
                />
              ) : (
                t('rules.no_match')
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('rules.column_code')}</TableHead>
                  <TableHead>{t('rules.column_label')}</TableHead>
                  <TableHead>{t('rules.column_control')}</TableHead>
                  <TableHead>{t('rules.column_asset_type')}</TableHead>
                  <TableHead>{t('rules.column_lifecycle')}</TableHead>
                  <TableHead>{t('rules.column_jurisdiction')}</TableHead>
                  <TableHead>{t('rules.column_severity')}</TableHead>
                  <TableHead>{t('rules.column_enabled')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRules.map((rule) => {
                  const sev = SEVERITY_CONFIG[rule.severity] || SEVERITY_CONFIG[3]
                  const kindResolved = (rule as { kind_resolved?: RuleKindResolved }).kind_resolved || 'recurring'
                  const kindCfg = KIND_RESOLVED_CONFIG[kindResolved]
                  return (
                    <TableRow
                      key={rule.uid}
                      className="cursor-pointer"
                      onClick={() => navigate({
                        to: '/$org/compliance-rules/$ruleUid',
                        params: { org: org || 'demo', ruleUid: rule.uid },
                      })}
                      data-testid={`rule-row-${rule.uid}`}
                    >
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{rule.code || '-'}</code>
                      </TableCell>
                      <TableCell className="font-medium">
                        {getLocalizedName(rule.label)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {controlMap.get(rule.compliance_control_uid) || rule.compliance_control_uid}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rule.asset_type_uid ? assetTypeMap.get(rule.asset_type_uid) || rule.asset_type_uid : t('rules.all_asset_types')}
                      </TableCell>
                      <TableCell>
                        <Badge className={kindCfg.color} data-testid={`rule-kind-${rule.uid}`}>
                          {t(kindCfg.i18nKey)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rule.jurisdiction === '*' ? t('rules.global_jurisdiction') : rule.jurisdiction}
                      </TableCell>
                      <TableCell>
                        <Badge className={sev.color}>{sev.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                          {rule.enabled ? t('rules.column_enabled') : t('common:status.disabled')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
